import React, { useContext, useEffect } from 'react';
import { useStyles } from './styles';
import { CreateUserContext } from '@/entities/context/createUserContext';
import { Flex, PasswordInput, Radio, SimpleGrid, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { RulesConditionList } from '@/containers/pages/register/components/PassRulesList';
import { useGenerateRules } from '@hooks/password/useGenerateRules';
import { IPasswordSecurity } from '@/entities/admin-app/filials';

interface ICreatePassword {
  isPasswordEnabled: boolean;
  passwordStrengthOptions?: IPasswordSecurity;
  setIsPasswordEnabled: (value: boolean) => void;
  disabled: boolean;
}

export const CreatePassword = ({
  isPasswordEnabled,
  setIsPasswordEnabled,
  passwordStrengthOptions,
  disabled
}: ICreatePassword) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 576px)');

  const { form } = useContext(CreateUserContext);

  useEffect(() => {
    if (!isPasswordEnabled) {
      form.clearFieldError('Credentials.Password');
      form.setFieldValue('Credentials.Password', '');
    }
  }, [isPasswordEnabled]);

  const password = form.getInputProps('Credentials.Password')?.value || '';

  const rules = useGenerateRules(password, passwordStrengthOptions);
  const onBlur = () => {
    if (!password) {
      form.getInputProps('Credentials.Password').onChange('');
    }
  };

  return (
    <Flex direction="column" mt="20px" gap={10}>
      <Text fz="14" fw="500" c="#4B4B4B">
        {t('login.password')}
      </Text>
      <Radio
        checked={!isPasswordEnabled}
        onChange={() => setIsPasswordEnabled(false)}
        className={classes.radio}
        label={t('profile.generatePassword')}
        data-cy="enableGeneratePassword"
      />
      <Radio
        checked={isPasswordEnabled}
        onChange={() => setIsPasswordEnabled(true)}
        className={classes.radio}
        label={t('profile.createPassword')}
        data-cy="enableCreatePassword"
      />
      <SimpleGrid cols={isMobile ? 1 : 2} w="100%">
        {isPasswordEnabled && (
          <>
            <PasswordInput
              {...form.getInputProps('Credentials.Password')}
              radius="md"
              className={classes.textFieldInput}
              placeholder={t('profile.password')}
              value={password || ''}
              onBlur={onBlur}
              withAsterisk
              data-cy="passwordInput"
              disabled={disabled}
            />
            <RulesConditionList rules={rules} check={password?.length > 0} />
          </>
        )}
      </SimpleGrid>
    </Flex>
  );
};
